import { Stack } from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import { useLearnerInfo, useLearnerPlanList } from "@/api/services/main/learner";
import { FooterContainer } from "@/components/commons/FooterContainer";
import { Button } from "@/components/elements/Button";
import { InnerBox } from "@/components/elements/InnerBox";
import { LoadingPage } from "@/components/elements/Loading/Loading";
import { Pagination } from "@/components/elements/Pagination";
import { TopPage } from "@/components/elements/TopPage";
import { DEFAULT_PER_PAGE, START_PAGE } from "@/constants/pagination";
import { divider } from "@/theme/colors";
import { usePagination } from "@/hooks/usePagination";
import { LearnerPlanListTable } from "@/features/learner/components/learner-plan-list/LearnerPlanListTable";
import { LearnerPlanListTop } from "@/features/learner/components/learner-plan-list/LearnerPlanListTop";
import { AppRoutes } from "@/routes/config";
import { getDynamicRoute } from "@/utils/url";

export const LearnerPlanListPage = () => {
  const navigate = useNavigate();
  const { learnerId } = useParams();
  const pagReq = usePagination();
  const page = pagReq.page || START_PAGE;
  const perPage = pagReq.perPage || DEFAULT_PER_PAGE;

  const { data: learnerRes, isLoading: isLoadingLearner } = useLearnerInfo();
  // Default to plan.createdAt DESC so the active plan lands at the top, then most-recent past plans.
  const sorts: `${string}|${string}`[] = pagReq.sorts && pagReq.sorts.length > 0 ? pagReq.sorts : ["plan.createdAt|DESC"];
  const { data: planListRes, isLoading: isLoadingPlans } = useLearnerPlanList(learnerId, {
    page,
    perPage,
    sorts,
  });

  const learner = learnerRes?.data;
  const plans = planListRes?.data ?? [];
  const pagination = planListRes?.pagination ?? {
    page,
    perPage,
    total: 0,
    totalPage: 0,
    nextPage: null,
    prevPage: null,
    count: 0,
  };

  const handleBack = () => {
    if (learnerId) {
      navigate(getDynamicRoute(AppRoutes.learnerDetail, { learnerId }));
    } else {
      navigate(AppRoutes.learnerList);
    }
  };

  if (isLoadingLearner) return <LoadingPage />;

  const fullName = learner?.fullName ?? "";

  return (
    <Stack minHeight="100%" gap={3}>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <TopPage
          title={`過去の計画一覧${fullName ? ` - ${fullName}` : ""}`}
          enablePageBack={false}
          description="過去に受験した際の記録です。受験実施結果や学習アプリの当時の状況を確認する際にご利用ください。"
        />
      </Stack>

      <InnerBox padding={3} showDivider={false} bordered borderColor={divider.high}>
        <Stack>
          <LearnerPlanListTop pagination={pagination} />
          <Stack spacing={3}>
            <LearnerPlanListTable
              rows={plans}
              fullName={fullName}
              industryName={learner?.industry?.name ?? ""}
              isLoading={isLoadingPlans}
            />
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Pagination totalPage={pagination.totalPage} />
            </Stack>
          </Stack>
        </Stack>
      </InnerBox>

      <FooterContainer>
        <Stack direction="row" gap={1} alignItems="center">
          <Button variant="ghost" size="sm" onClick={handleBack}>
            戻る
          </Button>
        </Stack>
      </FooterContainer>
    </Stack>
  );
};
